import { atLeast, duration, timeline, type MorseSpeed, type Span } from '@familyhub/shared';

/**
 * Plays morse three ways from one timeline (docs/MORSE.md §3): a sine tone
 * through Web Audio, a light (a callback the screen draws as a lamp), and the
 * phone's vibration motor. All three follow the same spans so they agree.
 *
 * Audio is scheduled on the AudioContext clock — sample-accurate, which a
 * setTimeout chain is not, and at 20 wpm a dot is 60 ms. The lamp follows on
 * timers, which is close enough for the eye.
 *
 * A vibration motor cannot render a 60 ms pulse distinctly, so when vibration
 * is on the whole timeline is scaled up (`atLeast`) and the tone and lamp slow
 * down with it — three channels in step beat a fast tone over a mushy buzz.
 */

export interface PlayOptions {
  speed: MorseSpeed;
  sound: boolean;
  flash: boolean;
  vibrate: boolean;
  toneHz: number;
  onLight?: (on: boolean) => void;
}

export interface Playback {
  done: Promise<void>;
  /** What the browser said to the vibration request: false is a refusal (no
   *  tap behind it, or a pattern it would not take); null when none was made.
   *  True is NOT proof of a buzz — Chrome on Android accepts the request and
   *  then stays still while the phone is on silent, and says nothing. */
  vibrated: boolean | null;
  stop: () => void;
}

/** Shortest pulse a phone motor renders as a distinct buzz. */
const MIN_VIBRATION_MS = 110;
/** Edge ramp so the tone starts and stops without a click. */
const RAMP_S = 0.005;

let ctx: AudioContext | null = null;

/**
 * Create/resume the AudioContext. Must run inside a tap — browsers refuse to
 * start audio otherwise — so the screen calls it on "Oefenen" and on every play
 * button; afterwards the lesson may auto-play the next question.
 */
export function unlockAudio(): void {
  const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
  if (!Ctor) return;
  ctx ??= new Ctor();
  if (ctx.state === 'suspended') void ctx.resume();
}

export function spansFor(text: string, opts: Pick<PlayOptions, 'speed' | 'vibrate'>): Span[] {
  const spans = timeline(text, opts.speed);
  return opts.vibrate ? atLeast(spans, MIN_VIBRATION_MS) : spans;
}

export function play(text: string, opts: PlayOptions): Playback {
  const spans = spansFor(text, opts);
  const total = duration(spans);
  const timers: ReturnType<typeof setTimeout>[] = [];
  let osc: OscillatorNode | null = null;
  let finish: () => void = () => {};
  const done = new Promise<void>((resolve) => (finish = resolve));

  if (opts.sound && ctx) {
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = opts.toneHz;
    osc.connect(gain);
    let t = ctx.currentTime + 0.05;
    for (const s of spans) {
      if (s.on) {
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.5, t + RAMP_S);
        gain.gain.setValueAtTime(0.5, t + s.ms / 1000 - RAMP_S);
        gain.gain.linearRampToValueAtTime(0, t + s.ms / 1000);
      }
      t += s.ms / 1000;
    }
    osc.start();
    osc.stop(t + 0.05);
  }

  let vibrated: boolean | null = null;
  if (opts.vibrate && typeof navigator.vibrate === 'function') {
    // The Vibration API's pattern is exactly our spans: buzz, pause, buzz, …
    vibrated = navigator.vibrate(spans.map((s) => Math.round(s.ms)));
  }

  if (opts.flash && opts.onLight) {
    let at = 50;
    for (const s of spans) {
      const on = s.on;
      timers.push(setTimeout(() => opts.onLight!(on), at));
      at += s.ms;
    }
    timers.push(setTimeout(() => opts.onLight!(false), at));
  }

  timers.push(setTimeout(finish, total + 120));

  return {
    done,
    vibrated,
    stop: () => {
      timers.forEach(clearTimeout);
      try {
        osc?.stop();
      } catch {
        // already stopped
      }
      if (opts.vibrate && typeof navigator.vibrate === 'function') navigator.vibrate(0);
      opts.onLight?.(false);
      finish();
    },
  };
}
